import React from 'react'
import { assocPath, identity, ifElse, is, of } from 'ramda'
import { FormInstance } from 'antd/lib/form'
import { TablePaginationConfig } from 'antd/lib/table'
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/lib/table/interface'
import { getRangeDate } from '@shared/utils/webHelper'

export type TableChangeHandler<T = any> = (
  pagination: TablePaginationConfig,
  filters: Record<string, FilterValue | null>,
  sorter: SorterResult<T> | SorterResult<T>[],
  extra: TableCurrentDataSource<T>
) => void

export type UseSearchProps = {
  form?: FormInstance
  initial?: { [key: string]: any }
  dateRangeField?: [string, string]
  dateFormat?: string
  transform?: (values: any) => any
  keepPreviousQuery?: boolean
}

export default function useSearch<T = any>({
  form,
  initial = {
    pageSize: 10,
  },
  dateFormat,
  dateRangeField = ['start', 'end'],
  transform = values => values,
  keepPreviousQuery = true,
}: UseSearchProps = {}) {
  const pageSize = initial.pageSize || 10

  const [query, setQuery] = React.useState<any>({
    ...initial,
    pageNum: 0,
    pageSize,
  })

  const [pageNum, setPageNum] = React.useState(1)

  const handleChange =
    <T = any>(field: string | string[]) =>
    (value: T, converter: (value: T) => any = (value: T) => value) => {
      const _field = ifElse(is(Array), identity, of)(field)

      setQuery(assocPath(_field, converter(value), query))
    }

  const handleSearch = () => {
    if (!form) {
      return
    }

    const { date = [], ...values } = form.getFieldsValue()
    const [startDate, endDate] = getRangeDate(date, dateFormat)
    const [startField, endField] = dateRangeField

    setPageNum(1)
    setQuery((prev: any) => {
      const q = {
        ...(keepPreviousQuery && prev),
        ...values,
        pageNum: 0,
        pageSize,
        [startField]: startDate,
        [endField]: endDate,
        reactQueryCache: new Date().getTime(),
      }

      if (transform) {
        return transform(q)
      }
      return q
    })
  }

  const handleTableChange: TableChangeHandler<T> = ({ current, pageSize }) => {
    if (!!current) {
      setPageNum(current)
      setQuery((prev: any) => {
        const q = {
          ...prev,
          current: Number(current) - 1,
          size: pageSize,
          pageNum: Number(current) - 1,
        }

        return q
      })
    }
  }

  return {
    query,
    pageNum,
    setQuery,
    setPageNum,
    handleChange,
    handleSearch,
    handleTableChange,
  }
}
